import Axios from "axios";
import useAnalytics from "hooks/analytics";
import React, { useEffect, useState } from "react";
import { useAsync } from "react-async-hook";
import { Loader } from "react-feather";
import { useHistory, useParams } from "react-router-dom";
import { UserActionEvent } from "utils/analytics/events";
import { Button, Footer, Header } from "../../components";
import useAuthToken from "../../hooks/auth-token";
import useLanguage from "../../hooks/language";
import ENDPOINTS from "../../utils/endpoints";
import ROUTES from "../../utils/routes";

const REDIRECT_DELAY_IN_S = 3;
const COUNTDOWN_DELAY_IN_MS = 1_000;

const EmailConfirmationPage: React.FunctionComponent = () => {
    const { token } = useParams<{ token: string }>();
    const { push } = useHistory();
    const [authToken, setAuthToken] = useAuthToken();
    const [countdown, setCountdown] = useState(REDIRECT_DELAY_IN_S);
    const { translations } = useLanguage();
    const t = translations.emailConfirmation;
    const [analytics] = useAnalytics();

    const { result, loading, error } = useAsync(async () => {
        const { data } = await Axios.put(ENDPOINTS.CONFIRM_EMAIL, {
            token,
        });

        const { token: userToken } = data;

        setAuthToken(userToken);

        return true;
    }, []);

    // NOTE: this allows the useAsync hook complete its call and the CDP to track the event below
    useEffect(() => {
        if (authToken) {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.EmailVerificationCompleted);
        }
    }, [authToken]);

    useEffect(() => {
        if (!result) {
            return;
        }

        if (countdown === 0) {
            push(ROUTES.HOME);
            return;
        }

        const countdownTimeout = window.setTimeout(() => {
            setCountdown((secondsRemaining) =>
                Math.max(secondsRemaining - 1, 0)
            );
        }, COUNTDOWN_DELAY_IN_MS);

        return () => {
            window.clearTimeout(countdownTimeout);
        };
    }, [countdown, push, result]);

    return (
        <div className="page">
            <Header />
            <div className="preauth-container">
                <h2>{t.header}</h2>
                {loading && <Loader className="spinorama" />}
                {error && (
                    <p
                        className="has-error"
                        dangerouslySetInnerHTML={{
                            __html: t.invalidTokenError,
                        }}
                    />
                )}
                {result && (
                    <div>
                        <div aria-live="polite">
                            {t.redirectMessage} <strong>{countdown}</strong>{" "}
                            {t.redirectMessageSeconds}.
                        </div>
                        <Button
                            type="secondary"
                            onClick={() => push(ROUTES.HOME)}
                        >
                            {t.goToHomeButton}
                        </Button>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default EmailConfirmationPage;
